/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import { decorate, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import c from 'classnames';
import _ from 'lodash';
import Header from '@amzn/base-ui/dist/parts/helpers/fields/Header';
import Description from '@amzn/base-ui/dist/parts/helpers/fields/Description';

import InstanceConfigPicker from '../parts/InstanceConfigPicker';
import { isHyperthreadingLockedInstanceType } from '../../../helpers/instance-config';

// Connects the shared instance configuration picker to the create form's envTypeConfigId field.
//
// expected props
// - configurations (via props) an array of the env type configurations MST
// - formField (via props) an instance of the mobx form field
// - handleHyperthreadingLock (via props) called with whether the picked config fixes hyperthreading
// - onChange (via props) called with the picked config id
// - fading (via props) dims the table while the region changes
// - region (via props) optional target AWS region for cost display
class SelectConfigurationTabs extends React.Component {
  get formField() {
    return this.props.formField;
  }

  get pickerConfigs() {
    return _.map(this.props.configurations, config => ({
      id: config.id,
      name: config.name,
      instanceType: config.instanceType,
      desc: config.desc,
      estimatedCostInfo: config.estimatedCostInfo,
      isCurrent: false,
    }));
  }

  handleSelect = config => {
    const field = this.formField;
    field.sync(config.id);
    field.resetValidation();
    if (_.isFunction(this.props.onChange)) {
      this.props.onChange(config.id);
    }
    if (_.isFunction(this.props.handleHyperthreadingLock)) {
      this.props.handleHyperthreadingLock(isHyperthreadingLockedInstanceType(config.instanceType));
    }
  };

  render() {
    const field = this.formField;
    const { error = '' } = field;
    const hasError = !_.isEmpty(error); // IMPORTANT do NOT use field.hasError

    return (
      <div className={c({ error: hasError, disabled: field.disabled })} style={{ marginTop: '20px' }}>
        <Header field={field} />
        <Description field={field} />
        <div className={c('config-table-region-transition', { 'config-table-region-fading': this.props.fading })}>
          <InstanceConfigPicker
            configs={this.pickerConfigs}
            selectedId={field.value}
            onSelect={this.handleSelect}
            groupName="envTypeConfigId"
            region={this.props.region}
            costHeader="Credit Usage"
            disabled={field.disabled}
          />
        </div>
        <div className="category-desc" style={{ marginTop: '12px' }}>
          Can&apos;t find what you are looking for? Try another region for additional availability and instance types,
          or contact race@rmit.edu.au for assistance.
        </div>
      </div>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(SelectConfigurationTabs, {
  formField: computed,
  pickerConfigs: computed,
  handleSelect: action,
});

export default observer(SelectConfigurationTabs);
